import React, { useMemo } from 'react';
import Table from 'components/Table';
import _ from 'lodash';
import { getRenderer } from 'components/Table/renderers';

import './table-result.less';
import { Tag } from 'antd';
import { fields } from 'shared/constants/results';
import { sum } from 'shared/constants/math';

function getSegmentTitle(segment) {
  return segment?.title || segment?.id;
}

function getSegmentId(segment) {
  return segment?.id;
}

function getRowId(row, dimensionIds, segmentId) {
  return Object.values({
    ..._.pick(row, dimensionIds),
    segmentId,
  }).join('-');
}

function getTableData({ primaryResult, results, dimensions, metrics, comparisonSegments }) {
  const dimensionIds = dimensions.map(d => d.name);
  const rows = [];

  for (const data of primaryResult.data) {
    const id = getRowId(data, dimensionIds, primaryResult.segmentId);
    const idWithoutSegment = getRowId(data, dimensionIds);

    const children = comparisonSegments?.map(s => {
      const segmentId = s.id;
      const segmentResult = results.find(r => r.segmentId === segmentId);
      const segmentRow = segmentResult?.data.find(r => getRowId(r, dimensionIds) === idWithoutSegment);
      return segmentRow ? {
        ...segmentRow,
        parentData: data,
        id: getRowId(segmentRow, dimensionIds, segmentId),
        segmentId,
      } : false;
    }).filter(Boolean);

    rows.push({
      id,
      ...data,
      segmentId: primaryResult.segmentId,
      children: children?.length ? children : null,
    });
  }

  if (primaryResult.statistics?.rowsBeforeLimitAtLeast > primaryResult.statistics?.rows) {
    const getOtherData = (totals, data) => {
      const otherData = {};

      for (const dimension of dimensions) {
        otherData[dimension.name] = <em>Other</em>;
      }

      for (const metric of metrics) {
        const sumValue = sum(data.map(d => d[metric.name]));

        if (metric.aggregation === 'count' || metric.aggregation === 'uniqExact' || metric.aggregation === 'sum') {
          otherData[metric.name] = totals[metric.name] - sumValue;
        }
        if (metric.aggregation === 'avg') {
          otherData[metric.name] = (primaryResult.statistics.rowsBeforeLimitAtLeast * totals[metric.name] - sumValue) / (primaryResult.statistics.rowsBeforeLimitAtLeast - data.length);
        }
      }
      return otherData;
    };

    const primaryResultOther = getOtherData(primaryResult.totals, primaryResult.data);

    const children = comparisonSegments?.map(s => {
      const segmentId = s.id;
      const segmentResult = results.find(r => r.segmentId === segmentId);
      return {
        id: `other-${segmentId}`,
        segmentId,
        parentData: primaryResultOther,
        ...getOtherData(segmentResult?.totals, segmentResult?.data),
      };
    }).filter(Boolean);

    rows.push({
      id: `other-${primaryResult.segmentId}`,
      segmentId: primaryResult.segmentId,
      ...primaryResultOther,
      children,
    });
  }

  return rows;
}

function getSegmentsColumnHeader({ segments, level = 0, getTitle = getSegmentTitle, ...props }) {
  if (segments.length > level) {
    const segment = segments[level];
    return [{
      title: getTitle(segment, level),
      ...props,
      children: segments.length > level + 1 ? getSegmentsColumnHeader({ segments, level: level + 1, getTitle, ...props }) : null,
    }];
  }

  return [];
}

function getMetricColumns({ metric, results, primaryResult, dimensions, segments }) {
  const cols = [];

  const fieldConfig = fields?.[metric.field];
  const type = metric.displayConfig?.format || fieldConfig?.type || 'number';
  const diffMetric = metric.displayConfig?.metrics?.[0];

  const getValue = (value, total) => {
    let result;
    if (!total) {
      return null;
    }
    if (type === 'percent') {
      result = value / total;
    } else {
      result = value - total;
    }
    return getRenderer(type)?.(result);
  };

  const renderShare = (getTotal) => (value, row) => {
    const parentValue = getTotal(row);
    if (!parentValue) {
      return null;
    }
    return getValue(row[diffMetric], parentValue);
  };

  const renderHeaderShare = (getTotal) => (s) => {
    const result = results?.find(r => r.segmentId === getSegmentId(s))?.totals?.[diffMetric];
    const total = getTotal(s);
    if (!total) {
      return null;
    }
    return getValue(result, total);
  };

  const columns = {
    value: {
      getTitle: (s) => {
        const result = results?.find(r => r.segmentId === getSegmentId(s))?.totals?.[metric.name];
        return result && (getRenderer(type)?.(result) || result);
      },
    },
    shareMainSegmentTotal: {
      getTitle: renderHeaderShare(() => primaryResult?.totals?.[diffMetric]),
      render: renderShare(() => primaryResult?.totals?.[diffMetric]),
    },
    shareSegmentTotal: {
      getTitle: renderHeaderShare((s) => results?.find(r => r.segmentId === getSegmentId(s))?.totals?.[diffMetric]),
      render: renderShare((row) => results?.find(r => r.segmentId === row.segmentId)?.totals?.[diffMetric]),
    },
    diffMainSegment: {
      getTitle: (s) => {
        const result = results?.find(r => r.segmentId === getSegmentId(s))?.totals?.[diffMetric];
        const total = primaryResult?.totals?.[diffMetric];
        return getValue(result, total);
      },
      render: (value, row) => {
        const result = row[diffMetric];
        const total = row.parentData?.[diffMetric];
        return getValue(result, total);
      },
    },
    rate: {
      getTitle: (s) => {
        const totals = results?.find(r => r.segmentId === getSegmentId(s))?.totals;
        let rate;
        if (metric.displayConfig?.metrics?.length === 2) {
          const [numerator, denominator] = metric.displayConfig.metrics;
          if (totals?.[numerator] && totals?.[denominator] !== 0) {
            rate = totals[numerator] / totals[denominator];
          }
        }
        return getRenderer(type)?.(rate);
      },
      render: (value, row) => {
        let rate;
        if (metric.displayConfig?.metrics?.length === 2) {
          const [numerator, denominator] = metric.displayConfig.metrics;
          if (row[numerator] && row[denominator] !== 0) {
            rate = row[numerator] / row[denominator];
          }
        }
        return getRenderer(type)?.(rate);
      },
    },
  };

  if (dimensions.length) {
    const children = getSegmentsColumnHeader({
      segments,
      dataIndex: metric.name,
      type,
      ...columns[metric.displayConfig?.displayType || 'value'],
    });

    cols.push({
      title: metric.label || metric.name,
      dataIndex: metric.name,
      type,
      align: children.length > 1 ? 'center' : 'right',
      children,
    });
  } else {
    cols.push({
      title: metric.label || metric.name,
      dataIndex: metric.name,
      type,
    });
  }

  return cols;
}

const TableResult = ({ results, resultConfig, loading, globalFilter, width }) => {
  const dimensions = resultConfig?.dimensions || [];
  const metrics = resultConfig?.metrics || [];
  const comparisonSegments = globalFilter?.comparisonSegments?.filter(s => s.enabled) || [];
  const mainSegment = globalFilter?.mainSegment;
  const segments = mainSegment ? [mainSegment, ...comparisonSegments] : [];

  const primaryResult = results?.find(r => r.segmentId === mainSegment.id);

  const tableData = useMemo(() => primaryResult && getTableData({ primaryResult, results, dimensions, metrics, comparisonSegments }), [results, dimensions, metrics, comparisonSegments, primaryResult]);

  const columns = useMemo(() => {
    const renderSegment = (value, level) => {
      const segment = segments.find(s => getSegmentId(s) === value);
      return segment ? <Tag color={segment?.color} style={{ marginLeft: level > 0 ? '1em' : '' }}>{getSegmentTitle(segment) || value}</Tag> : null;
    };

    const hasComparisonSegments = comparisonSegments.length;
    const fixedColumnWidth = width / 2 / (dimensions.length + (hasComparisonSegments ? 1 : 0));

    return [
      ...(hasComparisonSegments ? [{
        title: 'Segment',
        sorter: false,
        dataIndex: 'segmentId',
        children: dimensions.length ? getSegmentsColumnHeader({
          dataIndex: 'segmentId',
          segments: dimensions.length ? segments : [mainSegment],
          getTitle: (s, level) => renderSegment(s.id, level),
          render: renderSegment,
          fixed: 'left',
          width: fixedColumnWidth,
        }) : null,
        render: renderSegment,
        fixed: 'left',
      }] : []),
      ...dimensions.map(g => ({
        title: g.label || g.name,
        dataIndex: g.name,
        children: segments.length ? getSegmentsColumnHeader({
          getTitle: (s) => (hasComparisonSegments ? null : renderSegment(s?.id)),
          dataIndex: g.name,
          segments,
          fixed: 'left',
          render: (value) => value || <em>No value</em>,
          width: fixedColumnWidth,
        }) : undefined,
        fixed: 'left',
      })),
      ...metrics.reduce((cols, metric) => {
        if (metric.displayConfig?.displayType !== 'hidden') {
          cols.push(...getMetricColumns({ metric, results, primaryResult, dimensions, segments }));
        }
        return cols;
      }, []),
    ];
  }, [dimensions, metrics, results, primaryResult, segments, width]);

  return (
    <Table
      className="table-result"
      interactive={false}
      loading={loading}
      columns={columns}
      dataSource={tableData}
      expandable={{
        showExpandColumn: false,
        expandedRowKeys: tableData?.map(r => r.id),
      }}
      scroll={{
        x: 'max-content',
      }}
      pagination={resultConfig?.settings?.pageLimit ? {
        hideOnSinglePage: true,
        defaultPageSize: resultConfig?.settings?.pageLimit,
        pageSizeOptions: [5, 10, 50, 100],
      } : false}
    />
  );
};

export default TableResult;
